
import { computed, defineComponent, onMounted, ref, watch, provide } from "vue";
import PersonalDetails from "@/components/wizard/steps/PersonalDetails.vue";
import ProfessionalSummary from "@/components/wizard/steps/ProfessionalSummary.vue";
import ShowcaseYourExpertise from "@/components/wizard/steps/ShowcaseYourExpertise.vue";
import KeySkills from "@/components/wizard/steps/KeySkills.vue";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import objectPath from "object-path";
import { StepperComponent } from "@/assets/ts/components";

interface IStep1 {
  name: string;
  jobTitle: string;
  telephone: string;
  linkedInProfileUrl: string;
}

interface IStep2 {
  introduction: string;
  bio: string;
}

interface IStep3 {
  linkToExternalDocuments: string;
  documents: unknown;
}

interface IStep4 {
  expertIndustrySectors: Array<number | string>;
  expertExpertise: Array<number | string>;
}

interface CreateAccount extends IStep1, IStep2, IStep3, IStep4 {}

export default defineComponent({
  name: "expert-profile",
  components: {
    PersonalDetails,
    ProfessionalSummary,
    ShowcaseYourExpertise,
    KeySkills,
  },
  setup() {
    const showSuccess = ref<boolean>(false);
    const loading = ref<boolean>(false);
    const store = useStore();
    let roleId = 0;
    const formDataTemp = ref<FormData>(new FormData());
    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const user = computed(() => store.getters.getUser);
    const expertProfile = computed(() => store.getters.expertProfileGetter);
    const roles = computed(() => {
      return store.getters.getRolesData;
    });
    const doesExpertProfileExist = computed(() => {
      if (expertProfile.value) return true;
      else return false;
    });
    watch(user, () => {
      if (!Object.keys(user).length) {
        getExpertProfile();
      }
    });
    const getExpertProfile = async () => {
      if (!expertProfile.value)
        try {
          await store.dispatch(Actions.GET_EXPERT_PROFILE, {
            id: user.value.id,
          });
        } catch (err) {
          console.log(err);
        }
    };
    getExpertProfile();
    provide("expertProfile", expertProfile);
    const formData = ref<CreateAccount>({
      name: "",
      jobTitle: "",
      telephone: "",
      linkedInProfileUrl: "",
      introduction: "",
      bio: "",
      linkToExternalDocuments: "",
      documents: null,
      expertIndustrySectors: [],
      expertExpertise: [],
    });
    const cards = computed(() => store.getters.cardsGetter);
    const getCards = async () => {
      try {
        const response = await store.dispatch(Actions.GET_CARDS);
        console.log({ response });
      } catch (err) {
        //
      }
    };
    onMounted(async () => {
      try {
        if (!roles.value.length) {
          const response = await store.dispatch(Actions.GET_ROLES);
          if (response !== true) throw new Error();
          roleId = objectPath.get(
            roles.value.find((role) => role.name.toLowerCase() === "expert"),
            "id",
            0
          );
        } else
          roleId = objectPath.get(
            roles.value.find((role) => role.name.toLowerCase() === "expert"),
            "id",
            0
          );
        getCards();
      } catch (err) {
        console.log("error in fetching roles");
      }
      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );

      setCurrentPageBreadcrumbs("Expert Profile", []);
    });
    const createAccountSchema = [
      Yup.object({
        name: Yup.string().required().label("Name"),
        jobTitle: Yup.string().required().label("Job Title"),
        linkedInProfileUrl: Yup.string()
          .required()
          .label("LinkedIn Profile URL"),
      }),
      Yup.object({
        introduction: Yup.string().required().label("Introduction"),
        bio: Yup.string().required().label("Qualifications"),
      }),
      {},
      {},
    ];

    const currentSchema = computed(() => {
      return createAccountSchema[currentStepIndex.value];
    });

    const { resetForm, handleSubmit } = useForm<
      IStep1 | IStep2 | IStep3 | IStep4
    >({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });
    const toggleProfilePictureError = ref<boolean>(false);
    const handleStep = handleSubmit((values) => {
      if (!user.value?.profileImage) {
        toggleProfilePictureError.value = true;
        return;
      }
      formData.value = {
        ...formData.value,
        ...values,
      };

      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };
    const subscribe = async () => {
      loading.value = true;
      return new Promise((resolve, reject) => {
        store.dispatch(Actions.CREATE_PAYMENT).then((createPaymentResponse) => {
          if (
            createPaymentResponse.status == 400 ||
            createPaymentResponse.status == 500
          ) {
            store.commit("setAlert", {
              message: "Error",
              subMessage: createPaymentResponse?.data?.message ?? "",
              variant: "danger",
              duration: 4000,
              show: true,
            });
            reject();
            return;
          }
          store.commit("setAlert", {
            message: "Success",
            subMessage: "Subscription Successful!",
            variant: "primary",
            duration: 4000,
            show: true,
          });
          resolve("");
        });
      });
    };
    const formSubmit = async () => {
      try {
        const response = await store.dispatch(
          doesExpertProfileExist.value
            ? Actions.UPDATE_EXPERT_PROFILE
            : Actions.CREATE_EXPERT_PROFILE,
          doesExpertProfileExist.value
            ? { id: expertProfile.value.id, data: formDataTemp.value }
            : formDataTemp.value
        );
        if (response !== true) throw new Error();
        try {
          const response = await store.dispatch(Actions.ATTACH_ROLE, {
            id: roleId,
          });
          if (response !== true) throw new Error();
          if (!doesExpertProfileExist.value) {
            try {
              await subscribe();
              await store.dispatch(Actions.AUTH_USER);
            } catch (err) {
              loading.value = false;
              return;
            }
          }
          if (!user.value?.userPlan?.status ?? 0 == 0) {
            store.commit("setAlert", {
              message: "Kindly subscribe to a plan to continue",
              subMessage: "",
              variant: "danger",
              duration: 4000,
              show: true,
            });
            loading.value = false;
            return;
          }
          loading.value = true;
          await store.dispatch(Actions.AUTH_USER);
          loading.value = false;
          store.commit("setAlert", {
            message: "Success",
            subMessage: "Role is pending for approval",
            variant: "primary",
            duration: 4000,
            show: true,
          });
          showSuccess.value = true;
        } catch (err) {
          loading.value = false;
          const error = store.getters.getErrors;
          store.commit("setAlert", {
            message: "Error",
            subMessage: error,
            variant: "danger",
            duration: 4000,
            show: true,
          });
        }
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: `Expert Profile ${
            doesExpertProfileExist.value ? "Updated" : "Created"
          }`,
          variant: "primary",
          duration: 4000,
          show: true,
        });
        store.dispatch(Actions.GET_EXPERT_PROFILE, {
          id: user.value.id,
        });
      } catch (err) {
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: store.getters.getErrors,
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    };

    return {
      toggleProfilePictureError,
      showSuccess,
      loading,
      doesExpertProfileExist,
      formDataTemp,
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
    };
  },
});
